import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Breadcrumbs from '../components/Breadcrumbs';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import ProductCTAButtons from '../components/ProductCTAButtons';
import ProductExtraSections from '../components/ProductExtraSections';
import { PRODUCT_DETAILS } from '../data/products';
import { ProductHero } from '../components/product/ProductHero';
import { ProductOverview, FeatureGrid } from '../components/product/ProductOverview';
import { SplitSection, DuoPanels, ImageSplit } from '../components/product/SplitSections';
import { DarkCards, ListGroups, IconCards, ItemGrid } from '../components/product/GridSections';
import { SpecsSection, ContactSection, FaqSection, RelatedLinks } from '../components/product/FooterSections';
import { ProductReviews } from '../components/product/ProductReviews';
import GenericProductPage from './GenericProductPage';

const SECTION_COMPONENTS = {
  split: SplitSection,
  duoPanels: DuoPanels,
  imageSplit: ImageSplit,
  darkCards: DarkCards,
  listGroups: ListGroups,
  iconCards: IconCards,
  itemGrid: ItemGrid,
};

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCT_DETAILS[slug];
  const [liveAggregate, setLiveAggregate] = React.useState(null);
  if (!product) return <GenericProductPage slug={slug} />;

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.schema.name,
    description: product.schema.description,
    ...(product.schema.image ? { image: product.schema.image } : {}),
    brand: { '@type': 'Brand', name: 'Harpalyk' },
    category: product.schema.category,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'INR',
      price: product.schema.price,
      url: product.meta.canonical,
      availability: 'https://schema.org/InStock',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: liveAggregate ? String(liveAggregate.average) : product.schema.ratingValue,
      reviewCount: liveAggregate ? String(liveAggregate.count) : product.schema.reviewCount,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: product.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <>
      <Helmet>
        <title>{product.meta.title}</title>
        <meta name="description" content={product.meta.description} />
        <meta name="keywords" content={product.meta.keywords} />
        <link rel="canonical" href={product.meta.canonical} />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-20" data-testid={`${product.slug}-page`}>
        <Breadcrumbs items={[product.breadcrumb, { label: product.name }]} />
        <ProductHero product={product} />
        <ProductCTAButtons slug={product.slug} name={product.name} enquiryOnly={product.enquiryOnly} />
        <ProductOverview product={product} />
        <FeatureGrid product={product} />
        {product.sections.map((section, i) => {
          const Section = SECTION_COMPONENTS[section.type];
          return Section ? <Section key={`${section.type}-${i}`} section={section} accent={product.accent} /> : null;
        })}
        <SpecsSection product={product} />
        <ProductCTAButtons slug={product.slug} name={product.name} enquiryOnly={product.enquiryOnly} />
        <ContactSection product={product} />
        <FaqSection product={product} />
        <ProductReviews product={product} onAggregate={setLiveAggregate} />
        <RelatedLinks product={product} />
        <ProductExtraSections slug={product.slug} />
        <ProductCTAButtons slug={product.slug} name={product.name} enquiryOnly={product.enquiryOnly} />
      </main>

      <Footer />
      <WhatsAppWidget />
    </>
  );
}
